import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MessageSquare, ArrowLeft, Send, MoreVertical, AlertTriangle, Ban, X, Trash2, ChevronRight, Check, User, Building2, Crown, Briefcase, Image as ImageIcon } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { supabase } from '../utils/supabase/client';
import { toast } from '../utils/toast';
import { fetchWithAuth } from '../utils/api';
import { devLog, devError } from '../utils/logger';
import { canUploadVideo } from '../utils/accessPolicy';
import { triggerAndroidHaptic } from '../utils/haptics';

// ----- FAKE CONVERSATIONS (DEV/TEST) – fjern hele blokken under for å reversere -----
// Default: disabled. Enable only for explicit local testing.
const USE_FAKE_CONVERSATIONS = (import.meta.env.VITE_ENABLE_FAKE_CONVERSATIONS ?? 'false') === 'true';
function getFakeConversations(currentUserId: string): any[] {
  const names = [
    'Sandra Vik', 'Magnus Brekke',
    'Anna Larsen', 'Ola Nordmann', 'Kari Jensen', 'Erik Hansen', 'Maria Berg',
    'Lars Olsen', 'Sofie Dahl', 'Jonas Myhre', 'Emma Lund', 'Henrik Solberg',
    'Ida Nilsen', 'Martin Eide', 'Nora Kristiansen', 'Andreas Haugen', 'Maja Johansen',
    'Kristian Lie', 'Julie Andersen', 'Thomas Sand', 'Ingrid Holm', 'Per Moen',
  ];
  const snippets = [
    'Takk for svar!', 'Når kan du starte?', 'Vi tar kontakt snart.', 'Du: Hva med mandag?',
    'Interessant profil.', 'Send gjerne CV.', 'Møte i morgen?', 'Du: Tusen takk.',
    'Fint å høre fra deg.', 'Oppfølging neste uke.', 'Du: Jeg er interessert.',
    'Vi vurderer søknaden.', 'Ring meg i morgen.', 'Du: Kan du sende mer info?',
    'Bra å møte deg.', 'Kontrakt på plass snart.', 'Du: Håper å høre fra deg.',
    'Takk for at du søkte.', 'Vi har andre kandidater også.', 'Du: Lykke til videre.',
  ];
  const base = new Date();
  return names.map((name, i) => {
    const isEmployer = i % 3 === 0;
    const hasUnread = i % 4 === 0;
    const fromMe = i % 5 === 0;
    const createdAt = new Date(base.getTime() - (i + 1) * 3600000).toISOString();
    return {
      conversationId: `fake-conv-${i}`,
      userId: `fake-${i}`,
      user: {
        name,
        username: name.replace(/\s+/g, '').toLowerCase(),
        userType: isEmployer ? 'employer' : 'talent',
        profileImage: undefined,
      },
      lastMessage: {
        content: snippets[i % snippets.length],
        createdAt,
        senderId: fromMe ? currentUserId : `fake-${i}`,
      },
      unreadCount: hasUnread ? (i % 3) + 1 : 0,
    };
  });
}
// ----- END FAKE CONVERSATIONS -----

interface MessagesPageEnhancedProps {
  accessToken: string;
  currentUserId: string;
  initialRecipientId?: string;
  onBack: () => void;
  onViewProfile?: (userId: string) => void;
  onSelectConversation?: (userId: string) => void;
  onBackToList?: () => void;
  userType?: 'talent' | 'employer';
  /** Call when user has opened a chat and messages are loaded (so nav badge can refresh). */
  onUnreadCountShouldRefresh?: () => void;
  /** When true, the Messages view is the active tab (used to scroll conversation list to top on enter). */
  isVisible?: boolean;
}

export function MessagesPageEnhanced({ 
  accessToken, 
  currentUserId, 
  initialRecipientId, 
  onBack,
  onViewProfile,
  onSelectConversation,
  onBackToList,
  userType,
  onUnreadCountShouldRefresh,
  isVisible = true
}: MessagesPageEnhancedProps) {
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(initialRecipientId || null);
  const [messages, setMessages] = useState<any[]>([]);
  const [otherUser, setOtherUser] = useState<any>(null);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDescription, setReportDescription] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [reportingMessageId, setReportingMessageId] = useState<string | null>(null);
  const [showDeleteChatModal, setShowDeleteChatModal] = useState(false);
  const [currentGig, setCurrentGig] = useState<any>(null); // ✅ Store gig context
  const [imagePreview, setImagePreview] = useState<{ file: File; dataUrl: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputAreaContainerRef = useRef<HTMLDivElement>(null);
  const prevMessagesLengthRef = useRef<number>(0);
  // Flag to prevent multiple simultaneous scroll operations
  const isScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Debounce timer for scroll detection to prevent rapid state updates
  const scrollDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const realtimeRefreshTimerRef = useRef<number | null>(null);
  const lastRealtimeRefreshAtRef = useRef(0);
  const threadFallbackIntervalRef = useRef<number | null>(null);
  const typingIndicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const conversationRefreshTimerRef = useRef<number | null>(null);
  const conversationsListRef = useRef<HTMLDivElement>(null);
  const typingBroadcastChannelRef = useRef<any>(null);
  const localTypingActiveRef = useRef(false);
  const lastTypingBroadcastAtRef = useRef(0);
  const lastOnlineStatusSentAtRef = useRef(0);

  const enableBroadcastTyping = (import.meta.env.VITE_ENABLE_BROADCAST_TYPING ?? 'true') === 'true';

  const selectedConversationId = useMemo(() => {
    if (!selectedConversation) return null;
    const row = conversations.find((conv) => conv?.userId === selectedConversation);
    return row?.conversationId ?? null;
  }, [conversations, selectedConversation]);

  const isIosPwaList = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');

  // Autoscroll control
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showNewMessagesBadge, setShowNewMessagesBadge] = useState(false);
  // Measured height of fixed input area (warning + input) so chat padding stays above it
  const [inputAreaHeightPx, setInputAreaHeightPx] = useState(0);

  // Update selectedConversation when initialRecipientId changes
  useEffect(() => {
    setSelectedConversation(initialRecipientId || null);
  }, [initialRecipientId]);

  // Scroll conversation list to top when entering Messages view (in-app or from push).
  // C: synkront scrollTop = 0, deretter dobbel rAF + scrollTo; på iOS også forsinket retry (timing/layout).
  useEffect(() => {
    if (!isVisible || selectedConversation) return;
    const el = conversationsListRef.current;
    if (!el) return;
    const forceScrollTop = () => {
      el.scrollTop = 0;
      el.scrollTo(0, 0);
    };
    forceScrollTop();
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        forceScrollTop();
        devLog('📜 Messages: conversation list scrolled to top');
      });
    });
    const isIos = typeof document !== 'undefined' && (
      document.documentElement.classList.contains('ios-pwa') ||
      document.documentElement.classList.contains('ios')
    );
    const timeoutId = isIos ? window.setTimeout(forceScrollTop, 80) : undefined;
    return () => {
      cancelAnimationFrame(id);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [isVisible, selectedConversation]);

  // ✅ CRITICAL: Define scrollToBottom BEFORE useEffect hooks that use it
  // ✅ SIMPLIFIED: Use scrollTop for all platforms - more reliable than scrollIntoView on mobile
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (!messagesContainerRef.current) return;
    
    const container = messagesContainerRef.current;
    
    // Simple, reliable scroll using scrollTop for all platforms
    const performScroll = () => {
      if (!messagesContainerRef.current) return;
      
      const maxScroll = container.scrollHeight - container.clientHeight;
      const targetScrollTop = Math.max(0, maxScroll);
      
      if (behavior === 'smooth') {
        container.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      } else {
        // For 'auto', set immediately
        container.scrollTop = targetScrollTop;
      }
    };
    
    // Use requestAnimationFrame to ensure DOM is updated, then perform scroll
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        performScroll();
      });
    });
  }, []);

  const adjustMessageInputHeight = useCallback(() => {
    const el = messageInputRef.current;
    if (!el) return;

    el.style.height = 'auto';
    const maxHeightPx = 160;
    const nextHeight = Math.min(el.scrollHeight, maxHeightPx);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeightPx ? 'auto' : 'hidden';
  }, []);

  // Refetch conversations when accessToken changes (e.g. after re-login) so list is not empty
  useEffect(() => {
    if (!accessToken) return;
    fetchConversations();

    // Update online status periodically (server-side throttling also applies).
    const onlineInterval = setInterval(updateOnlineStatus, 300000);
    updateOnlineStatus();

    return () => clearInterval(onlineInterval);
  }, [accessToken]);

  useEffect(() => {
    if (selectedConversation) {
      // Reset message count when switching conversations
      // Don't reset prevMessagesLengthRef here - let it be set after fetchMessages completes
      setShowNewMessagesBadge(false);
      setIsNearBottom(true); // Reset to bottom when opening new conversation
      isScrollingRef.current = false; // Reset scroll flag
      setIsTyping(false);
      // Local typing is per-thread; reset on switch to avoid stale state.
      localTypingActiveRef.current = false;
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      
      // Clear any pending scroll operations
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      
      fetchMessages(selectedConversation);
      checkBlockedStatus(selectedConversation);

      return () => {
        // Cleanup scroll debounce when switching conversations
        if (scrollDebounceRef.current) {
          clearTimeout(scrollDebounceRef.current);
          scrollDebounceRef.current = null;
        }
        if (typingIndicatorTimeoutRef.current) {
          clearTimeout(typingIndicatorTimeoutRef.current);
          typingIndicatorTimeoutRef.current = null;
        }
      };
    }
  }, [selectedConversation]);

  // Keep conversations list fresh even when no thread is selected.
  // Fixes delay before a recreated conversation appears after deletion.
  useEffect(() => {
    if (!currentUserId || !accessToken) return;

    const scheduleConversationRefresh = (reason: string) => {
      if (conversationRefreshTimerRef.current !== null) {
        window.clearTimeout(conversationRefreshTimerRef.current);
      }
      conversationRefreshTimerRef.current = window.setTimeout(() => {
        fetchConversations();
        onUnreadCountShouldRefresh?.();
        devLog('💬 Conversations realtime refresh:', reason);
        conversationRefreshTimerRef.current = null;
      }, 140);
    };

    const listChannel = supabase
      .channel(`chat-conversations:${currentUserId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${currentUserId}`,
        },
        () => {
          scheduleConversationRefresh('messages:incoming:list');
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') devLog('✅ Chat conversations realtime subscribed');
        if (status === 'CHANNEL_ERROR') devError('❌ Chat conversations realtime error');
      });

    return () => {
      supabase.removeChannel(listChannel);
      if (conversationRefreshTimerRef.current !== null) {
        window.clearTimeout(conversationRefreshTimerRef.current);
        conversationRefreshTimerRef.current = null;
      }
    };
  }, [currentUserId, accessToken, onUnreadCountShouldRefresh]);

  // Realtime sync for active thread (replaces high-frequency polling).
  useEffect(() => {
    if (!selectedConversation || !currentUserId || !accessToken) return;

    const startThreadFallback = () => {
      if (threadFallbackIntervalRef.current !== null) return;
      devLog('⚠️ Chat thread realtime degraded - starting controlled fallback polling');
      threadFallbackIntervalRef.current = window.setInterval(() => {
        fetchMessages(selectedConversation, true);
        fetchConversations();
        onUnreadCountShouldRefresh?.();
      }, 45000);
    };

    const stopThreadFallback = () => {
      if (threadFallbackIntervalRef.current === null) return;
      window.clearInterval(threadFallbackIntervalRef.current);
      threadFallbackIntervalRef.current = null;
      devLog('✅ Chat thread realtime recovered - stopping fallback polling');
    };

    const scheduleThreadRefresh = (reason: string) => {
      const now = Date.now();
      const elapsed = now - lastRealtimeRefreshAtRef.current;
      const MIN_REFRESH_GAP_MS = 500;

      if (elapsed < MIN_REFRESH_GAP_MS) return;

      if (realtimeRefreshTimerRef.current !== null) {
        window.clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }

      realtimeRefreshTimerRef.current = window.setTimeout(() => {
        lastRealtimeRefreshAtRef.current = Date.now();
        fetchMessages(selectedConversation, true);
        fetchConversations();
        onUnreadCountShouldRefresh?.();
        devLog('💬 Realtime thread refresh:', reason);
        realtimeRefreshTimerRef.current = null;
      }, 120);
    };

    const threadChannel = supabase
      .channel(`chat-thread:${currentUserId}:${selectedConversation}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${currentUserId}`,
        },
        (payload: any) => {
          const senderId = payload?.new?.sender_id || payload?.old?.sender_id;
          if (senderId === selectedConversation) {
            scheduleThreadRefresh('messages:incoming');
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'messages',
          filter: `sender_id=eq.${currentUserId}`,
        },
        (payload: any) => {
          const receiverId = payload?.new?.receiver_id || payload?.old?.receiver_id;
          if (receiverId === selectedConversation) {
            scheduleThreadRefresh('messages:outgoing');
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          devLog('✅ Chat thread realtime subscribed');
          stopThreadFallback();
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          devError('❌ Chat thread realtime error:', status);
          startThreadFallback();
        }
      });

    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        scheduleThreadRefresh('app:visible');
      }
    };

    const handleOnline = () => {
      scheduleThreadRefresh('network:online');
    };

    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    window.addEventListener('online', handleOnline);

    return () => {
      stopThreadFallback();
      supabase.removeChannel(threadChannel);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      window.removeEventListener('online', handleOnline);
      if (realtimeRefreshTimerRef.current !== null) {
        window.clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }
    };
  }, [selectedConversation, currentUserId, accessToken, onUnreadCountShouldRefresh]);

  // Realtime typing indicator for active conversation.
  useEffect(() => {
    if (!enableBroadcastTyping) return;
    if (!selectedConversation || !currentUserId) return;
    if (!selectedConversationId) return;

    // Ensure we don't keep stale channels when switching threads.
    if (typingBroadcastChannelRef.current) {
      supabase.removeChannel(typingBroadcastChannelRef.current);
      typingBroadcastChannelRef.current = null;
    }

    const channelName = `chat-typing:conv:${selectedConversationId}`;
    const typingChannel = supabase
      .channel(channelName)
      .on('broadcast', { event: 'typing' }, ({ payload }: any) => {
        const senderUserId = payload?.senderUserId as string | undefined;
        const isTypingPayload = payload?.isTyping === true;

        // Ignore our own broadcasts and anything not from the currently selected peer.
        if (!senderUserId || senderUserId === currentUserId) return;
        if (senderUserId !== selectedConversation) return;

        if (isTypingPayload) {
          setIsTyping(true);
          if (typingIndicatorTimeoutRef.current) {
            clearTimeout(typingIndicatorTimeoutRef.current);
          }
          typingIndicatorTimeoutRef.current = setTimeout(() => {
            setIsTyping(false);
          }, 5500);
        } else {
          setIsTyping(false);
        }
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') devLog('✅ Chat typing broadcast subscribed');
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          devError('❌ Chat typing broadcast channel error:', status);
        }
      });

    typingBroadcastChannelRef.current = typingChannel;

    return () => {
      supabase.removeChannel(typingChannel);
      if (typingBroadcastChannelRef.current === typingChannel) {
        typingBroadcastChannelRef.current = null;
      }
      if (typingIndicatorTimeoutRef.current) {
        clearTimeout(typingIndicatorTimeoutRef.current);
        typingIndicatorTimeoutRef.current = null;
      }
      setIsTyping(false);
    };
  }, [enableBroadcastTyping, selectedConversation, selectedConversationId, currentUserId]);

  // Realtime blocked status updates for active conversation.
  useEffect(() => {
    if (!selectedConversation || !currentUserId) return;

    const blockedChannel = supabase
      .channel(`chat-blocked:${currentUserId}:${selectedConversation}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'blocked_users',
          filter: `blocker_id=eq.${currentUserId}`,
        },
        (payload: any) => {
          const changedUserId = payload?.new?.blocked_id || payload?.old?.blocked_id;
          if (changedUserId === selectedConversation) {
            checkBlockedStatus(selectedConversation);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'blocked_users',
          filter: `blocked_id=eq.${currentUserId}`,
        },
        (payload: any) => {
          const changedUserId = payload?.new?.blocker_id || payload?.old?.blocker_id;
          if (changedUserId === selectedConversation) {
            checkBlockedStatus(selectedConversation);
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') devLog('✅ Chat blocked realtime subscribed');
        if (status === 'CHANNEL_ERROR') devError('❌ Chat blocked realtime error');
      });

    return () => {
      supabase.removeChannel(blockedChannel);
    };
  }, [selectedConversation, currentUserId, accessToken]);

  useEffect(() => {
    // Check if new messages arrived (not just initial load or user scrolling)
    const currentLength = messages.length;
    const previousLength = prevMessagesLengthRef.current;
    const hasNewMessages = currentLength > previousLength;
    const wasEmpty = previousLength === 0;
    
    // Update ref AFTER checking (but before potential scroll)
    // This prevents double-triggering on the same message set
    if (currentLength !== previousLength) {
      prevMessagesLengthRef.current = currentLength;
    }
    
    // Check if the last message is from current user (they just sent it)
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    const isOwnMessage = lastMessage && lastMessage.senderId === currentUserId;
    
    // ✅ Only autoscroll when we actually added new messages (not on every poll/re-render):
    // 1. Initial load (wasEmpty) - instant scroll
    // 2. New message(s) and last is from current user (we just sent) - smooth scroll
    // 3. New message(s) from other and user is near bottom - smooth scroll
    // Never scroll just because last message is ours (fixes scroll-back when user scrolled up and polling runs)
    const shouldAutoScroll = (wasEmpty || (hasNewMessages && (isOwnMessage || isNearBottom))) && messages.length > 0;
    
    if (shouldAutoScroll) {
      // Clear any pending scroll operations
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      // Platform-specific delay for better reliability
      const isIOS = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
      const isAndroid = typeof window !== 'undefined' && /android/i.test(navigator.userAgent) && !document.documentElement.classList.contains('ios-pwa');
      
      // Instant scroll on initial load, smooth for everything else
      const delay = wasEmpty ? 0 : (isIOS ? 100 : isAndroid ? 150 : 50);
      const behavior: ScrollBehavior = wasEmpty ? 'auto' : 'smooth';
      
      scrollTimeoutRef.current = setTimeout(() => {
        scrollToBottom(behavior);
        setShowNewMessagesBadge(false);
        
        // Mark as near bottom when we scrolled (initial load or new messages)
        if (wasEmpty || hasNewMessages) {
          setIsNearBottom(true);
        }
      }, delay);
    } else if (hasNewMessages && messages.length > 0 && !isOwnMessage) {
      // Only show badge if new messages arrived from other person AND user has scrolled up
      setShowNewMessagesBadge(true);
    }
    
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
      // Note: Don't clear scrollDebounceRef here - it's managed by handleScroll cleanup
    };
  }, [messages, isNearBottom, scrollToBottom, currentUserId]);

  useEffect(() => {
    if (!isTyping || !isNearBottom || !selectedConversation) return;

    const id = window.setTimeout(() => {
      scrollToBottom('smooth');
    }, 80);

    return () => window.clearTimeout(id);
  }, [isTyping, isNearBottom, scrollToBottom, selectedConversation]);

  useEffect(() => {
    adjustMessageInputHeight();
  }, [messageInput, adjustMessageInputHeight]);
  
  // Detect if user is near bottom of chat - stricter threshold (80px) to prevent unwanted autoscroll
  // Debounced to prevent rapid state updates during scrolling
  const handleScroll = () => {
    if (!messagesContainerRef.current) return;
    
    // Clear any pending debounce
    if (scrollDebounceRef.current) {
      clearTimeout(scrollDebounceRef.current);
    }
    
    // Debounce scroll detection to avoid rapid state flips
    scrollDebounceRef.current = setTimeout(() => {
      if (!messagesContainerRef.current) return;
      
      const element = messagesContainerRef.current;
      // ✅ STRICTER THRESHOLD: Only 80px from bottom = "near bottom"
      // This prevents autoscroll when user has scrolled up to read older messages
      const scrollDistanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
      const isAtBottom = scrollDistanceFromBottom < 80; // Within 80px of bottom = near bottom
      setIsNearBottom(isAtBottom);
      
      // Hide badge when user scrolls to bottom manually
      if (isAtBottom) {
        setShowNewMessagesBadge(false);
      }
    }, 100); // 100ms debounce
  };
  
  // Scroll to bottom and hide badge when clicking badge
  const handleNewMessagesBadgeClick = () => {
    triggerAndroidHaptic('light');
    scrollToBottom();
    setShowNewMessagesBadge(false);
    setIsNearBottom(true);
  };

  // Measure fixed input area height so messages container padding keeps chat above it (all platforms)
  useEffect(() => {
    const el = inputAreaContainerRef.current;
    if (!el || !selectedConversation) return;

    const updateHeight = () => {
      const h = el.getBoundingClientRect().height;
      setInputAreaHeightPx(Math.round(h));
    };

    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedConversation]);

  const updateOnlineStatus = async () => {
    try {
      if (!accessToken) return;
      if (document.visibilityState !== 'visible') return;

      const now = Date.now();
      // Avoid excessive profile writes (last_active) across long sessions.
      if (now - lastOnlineStatusSentAtRef.current < 5 * 60 * 1000) return;
      lastOnlineStatusSentAtRef.current = now;

      await fetch(
        `${API_BASE}/online-status`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
    } catch (error) {
      devLog('Error updating online status:', error);
    }
  };

  const checkBlockedStatus = async (userId: string) => {
    try {
      const response = await fetch(
        `${API_BASE}/blocked/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setIsBlocked(data.isBlocked);
      }
    } catch (error) {
      devLog('Error checking blocked status:', error);
    }
  };

  const sendTypingBroadcast = useCallback(
    async (typing: boolean) => {
      if (!enableBroadcastTyping) return;
      if (!selectedConversationId) return;
      if (!typingBroadcastChannelRef.current) return;

      const now = Date.now();
      // Guard: do not spam broadcasts during fast input loops.
      if (typing && now - lastTypingBroadcastAtRef.current < 800) return;
      lastTypingBroadcastAtRef.current = now;

      try {
        await typingBroadcastChannelRef.current.send({
          type: 'broadcast',
          event: 'typing',
          payload: {
            senderUserId: currentUserId,
            isTyping: typing,
            ts: now,
          },
        });
      } catch (error) {
        devLog('Error sending typing broadcast:', error);
      }
    },
    [currentUserId, enableBroadcastTyping, selectedConversationId]
  );

  const stopLocalTyping = useCallback(
    (reason: string) => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
      if (!localTypingActiveRef.current) return;
      localTypingActiveRef.current = false;
      void sendTypingBroadcast(false);
      devLog('⌨️ Typing stopped:', reason);
    },
    [sendTypingBroadcast]
  );

  const startLocalTyping = useCallback(() => {
    if (localTypingActiveRef.current) return;
    localTypingActiveRef.current = true;
    void sendTypingBroadcast(true);
  }, [sendTypingBroadcast]);

  const handleInputChange = (value: string) => {
    setMessageInput(value);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    if (value.trim()) {
      startLocalTyping();
      // Stop typing after idle period (no more keystrokes).
      typingTimeoutRef.current = setTimeout(() => {
        stopLocalTyping('idle-timeout');
      }, 3000);
    } else {
      stopLocalTyping('cleared');
    }
  };

  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Ugyldig bildeformat. Kun JPEG, PNG, GIF og WebP er tillatt.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Bildet er for stort. Maks 10MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setImagePreview({ file, dataUrl });
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const fetchConversations = async () => {
    try {
      devLog('📨 Fetching conversations...');
      const response = await fetchWithAuth(
        `${API_BASE}/conversations`,
        { cache: 'no-store' }
      );

      devLog('📨 Conversations response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        devLog('📨 Conversations data:', data);
        devLog('📨 Conversations count:', data.conversations?.length || 0);
        
        // Sort conversations by last message date, newest first
        const sortedConversations = (data.conversations || []).sort((a: any, b: any) => {
          const dateA = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
          const dateB = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
          return dateB - dateA; // Newest first
        });
        const list = USE_FAKE_CONVERSATIONS
          ? [...getFakeConversations(currentUserId), ...sortedConversations]
          : sortedConversations;
        devLog('📨 Setting conversations:', list.length, USE_FAKE_CONVERSATIONS ? '(incl. fake)' : '');
        setConversations(list);
      } else {
        devError('❌ Conversations fetch failed:', response.status, await response.text());
        if (USE_FAKE_CONVERSATIONS) {
          setConversations(getFakeConversations(currentUserId));
          devLog('📨 Showing fake conversations (API failed)');
        }
      }
    } catch (error) {
      devError('❌ Error fetching conversations:', error);
      if (USE_FAKE_CONVERSATIONS) {
        setConversations(getFakeConversations(currentUserId));
        devLog('📨 Showing fake conversations (fetch error)');
      }
      // If session expired, user will be logged out by fetchWithAuth
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (userId: string, silent = false) => {
    // Only show loading on initial fetch, not on auto-refresh
    if (!silent) {
      setLoadingMessages(true);
    }
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/messages/${userId}`
      );

      if (response.ok) {
        const data = await response.json();
        const newMessages = data.messages || [];
        
        setMessages(newMessages);
        setOtherUser(data.otherUser);
        setCurrentGig(data.gig || null); // ✅ Set gig context if exists
        setLoadingMessages(false);
        
        // ✅ FIX: Update conversation list when receiving new messages (even during auto-polling)
        if (silent && newMessages.length > 0) {
          const lastMessage = newMessages[newMessages.length - 1];
          
          // Only update conversation list if the last message is from the other user
          if (lastMessage.senderId !== currentUserId) {
            setConversations(prev => {
              const updatedConversations = prev.map(conv => 
                conv.userId === userId
                  ? {
                      ...conv,
                      lastMessage: {
                        content: lastMessage.content,
                        createdAt: lastMessage.createdAt,
                        senderId: lastMessage.senderId,
                      },
                    }
                  : conv
              );
              
              // Sort to move updated conversation to top
              return updatedConversations.sort((a, b) => {
                const dateA = a.lastMessage?.createdAt ? new Date(a.lastMessage.createdAt).getTime() : 0;
                const dateB = b.lastMessage?.createdAt ? new Date(b.lastMessage.createdAt).getTime() : 0;
                return dateB - dateA;
              });
            });
          }
        }
        
        // Refresh conversations to update unread counts on initial load
        if (!silent) {
          fetchConversations();
          onUnreadCountShouldRefresh?.();
          // Scroll to bottom when opening a conversation so latest messages are visible
          setTimeout(() => scrollToBottom('auto'), 150);
        }
      }
    } catch (error) {
      devLog('Error fetching messages:', error);
      setLoadingMessages(false); // Also set to false on error
      // If session expired, user will be logged out by fetchWithAuth
    }
  };

  const sendMessage = async () => {
    const hasText = messageInput.trim().length > 0;
    const hasImage = !!imagePreview;
    if ((!hasText && !hasImage) || !selectedConversation || isBlocked) return;
    triggerAndroidHaptic('light');

    const messageContent = messageInput.trim();
    const tempId = `temp-${Date.now()}`;
    
    // Optimistic UI: Add message immediately
    const optimisticMessage: Record<string, unknown> = {
      id: tempId,
      senderId: currentUserId,
      recipientId: selectedConversation,
      content: hasText ? messageContent : '[Bilde]',
      createdAt: new Date().toISOString(),
      read: false,
      pending: true,
    };
    if (imagePreview) {
      optimisticMessage.imageUrl = imagePreview.dataUrl;
    }
    
    setMessages(prev => [...prev, optimisticMessage]);
    setMessageInput('');
    messageInputRef.current?.blur(); // Close keyboard on mobile

    // ✅ ALWAYS scroll when user sends a message - smooth scroll to bottom
    // Mark as near bottom so incoming messages will also autoscroll
    setIsNearBottom(true);
    
    // Clear any pending scroll operations
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Smooth scroll to bottom after sending message
    const isIOS = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    const isAndroid = typeof window !== 'undefined' && /android/i.test(navigator.userAgent) && !document.documentElement.classList.contains('ios-pwa');
    const delay = isIOS ? 50 : isAndroid ? 100 : 30;
    
    scrollTimeoutRef.current = setTimeout(() => {
      scrollToBottom('smooth');
      setShowNewMessagesBadge(false);
    }, delay);

    setSending(true);
    // Clear typing indicator
    stopLocalTyping('sendMessage');
    
    const body: { recipientId: string; content?: string; imageBase64?: string } = {
      recipientId: selectedConversation,
    };
    if (hasText) body.content = messageContent;
    if (imagePreview) body.imageBase64 = imagePreview.dataUrl;
    
    try {
      const response = await fetchWithAuth(
        `${API_BASE}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }
      );

      if (response.ok) {
        setImagePreview(null); // Clear only on success
        const data = await response.json();
        
        // ✅ FIX: Remove optimistic message and add real message to avoid race condition with polling
        setMessages(prev => [
          ...prev.filter(m => m.id !== tempId),
          data.message
        ]);
        
        // ✅ CRITICAL: Only scroll if user is still near bottom (let useEffect handle it)
        // Don't force scroll here - let the useEffect autoscroll logic handle it based on isNearBottom
        
        // ✅ Update conversation list in place (no re-sort) so list doesn't jump to top then back
        const displayContent = hasText ? messageContent : '[Bilde]';
        setConversations(prev => {
          const now = new Date().toISOString();
          return prev.map(conv =>
            conv.userId === selectedConversation
              ? {
                  ...conv,
                  lastMessage: {
                    content: displayContent,
                    createdAt: now,
                    senderId: currentUserId,
                  },
                }
              : conv
          );
        });
        
        // Refresh list from backend after a short delay so order stays consistent (one update, no double jump)
        setTimeout(() => fetchConversations(), 500);
      } else {
        // Remove optimistic message on error (imagePreview kept for retry)
        setMessages(prev => prev.filter(m => m.id !== tempId));
        const errorData = await response.json();
        if (response.status === 403) {
          toast.error(errorData?.error || 'Kan ikke sende melding til denne brukeren');
        } else if (response.status === 400) {
          toast.error(errorData?.error || 'Meldingen kunne ikke sendes');
        } else {
          toast.error(errorData?.error || 'Kunne ikke sende melding');
        }
      }
    } catch (error) {
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempId));
      devLog('Error sending message:', error);
      const errorMessage =
        error instanceof Error ? error.message : typeof error === 'string' ? error : '';
      if (/cannot send message|kan ikke sende melding til denne brukeren|denne brukeren er utilgjengelig/i.test(errorMessage)) {
        toast.error('Denne brukeren er utilgjengelig');
      } else if (!errorMessage || /failed to fetch|network/i.test(errorMessage)) {
        // Network-style failures may not show a toast in fetchWithAuth.
        toast.error('Kunne ikke sende melding');
      }
    } finally {
      setSending(false);
    }
  };

  const blockUser = async () => {
    if (!selectedConversation) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/block`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            userId: selectedConversation,
          }),
        }
      );

      if (response.ok) {
        setIsBlocked(true);
        setShowUserMenu(false);
        toast.success('Bruker blokkert');
      }
    } catch (error) {
      devLog('Error blocking user:', error);
    }
  };

  const unblockUser = async () => {
    if (!selectedConversation) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/unblock`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            userId: selectedConversation,
          }),
        }
      );

      if (response.ok) {
        setIsBlocked(false);
        setShowUserMenu(false);
        toast.success('Blokkering opphevet');
      }
    } catch (error) {
      devLog('Error unblocking user:', error);
    }
  };

  const reportUser = async () => {
    if (!selectedConversation || !reportReason.trim()) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            reportedUserId: selectedConversation,
            reason: reportReason,
            description: reportDescription,
          }),
        }
      );

      if (response.ok) {
        setShowReportModal(false);
        setShowUserMenu(false);
        setReportReason('');
        setReportDescription('');
        toast.success('Rapportering sendt', 'Takk for tilbakemeldingen');
      } else {
        const errorData = await response.json().catch(() => null);
        toast.error(errorData?.error || 'Kunne ikke sende rapport');
      }
    } catch (error) {
      devLog('Error reporting user:', error);
      toast.error('Noe gikk galt');
    }
  };

  const deleteConversation = async () => {
    if (!selectedConversation) return;
    
    try {
      const response = await fetch(
        `${API_BASE}/conversation/${selectedConversation}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        setShowDeleteChatModal(false);
        setShowUserMenu(false);
        setSelectedConversation(null);
        fetchConversations();
        toast.success('Samtale slettet');
        onBackToList?.();
      }
    } catch (error) {
      devLog('Error deleting conversation:', error);
      toast.error('Kunne ikke slette samtalen');
    }
  };

  const avatarColors = [
    'from-purple-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-orange-500 to-red-500',
    'from-green-500 to-emerald-500',
    'from-yellow-500 to-orange-500',
    'from-indigo-500 to-purple-500',
  ];

  const getAvatarColor = (name: string) => {
    const index = name?.charCodeAt(0) % avatarColors.length || 0;
    return avatarColors[index];
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return days === 1 ? 'I går' : `${days} dager siden`;
    }
    if (hours > 0) {
      return `${hours} timer siden`;
    }
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes > 0) {
      return `${minutes} min siden`;
    }
    return 'Nå';
  };

  // Handle conversation selection - calls parent callback to update URL
  const handleSelectConversation = (userId: string) => {
    triggerAndroidHaptic('light');
    if (onSelectConversation) {
      // Let parent (App.tsx) handle URL/history
      onSelectConversation(userId);
    } else {
      // Fallback for local state if no callback
      setSelectedConversation(userId);
    }
  };

  // Handle closing conversation - calls parent callback to go back to list
  const handleCloseConversation = () => {
    triggerAndroidHaptic('light');
    if (onBackToList) {
      // Let parent (App.tsx) handle URL/history
      onBackToList();
    } else {
      // Fallback for local state if no callback
      setSelectedConversation(null);
    }
  };

  return (
    <div className="h-screen bg-white overflow-hidden xl:fixed xl:inset-0 xl:z-30 xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
        {/* Wrapper - on desktop: 22px padding from global header (4rem), flex center; card fixed height so chat can scroll */}
        <div className="h-full xl:flex xl:items-center xl:justify-center xl:min-h-0 xl:pt-[calc(4rem+22px)] xl:pb-[22px] xl:px-[22px] xl:max-w-7xl xl:mx-auto xl:w-full">
          {/* Content container - fullscreen on mobile; on desktop fixed height so grid/chat get bounded height and scroll */}
          <div className="bg-white xl:rounded-3xl xl:shadow-lg overflow-hidden xl:border-2 xl:border-purple-100 flex flex-col h-full xl:h-[calc(100vh-4rem-44px)] xl:max-h-[calc(100vh-4rem-44px)] xl:w-full xl:min-h-0">
            <div className="flex xl:grid xl:grid-cols-3 xl:grid-rows-1 flex-1 h-full min-h-0 overflow-hidden">
          {/* Conversations list */}
          <div className={`border-r border-purple-100 flex flex-col h-full min-h-0 w-full xl:w-auto ${selectedConversation ? 'hidden xl:flex' : 'flex'}`}>
            {/* Header - KUN DESKTOP */}
            <div className="hidden xl:block p-4 sm:p-6 border-b border-purple-100 flex-shrink-0">
              <h2 className="text-xl sm:text-2xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Meldinger
              </h2>
            </div>
            
            {/* Mobile "Velg en samtale" banner - fast under global header, kun listen scrolles */}
            <div
              className="xl:hidden fixed left-0 right-0 z-10 h-16 flex items-center px-4 border-b border-purple-100 bg-purple-50"
              style={{ top: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}
            >
              <div className="flex items-center gap-2 text-purple-700">
                <MessageSquare className="w-5 h-5" />
                <p className="text-sm font-medium">Velg en samtale for å starte</p>
              </div>
            </div>
            
            {/* Conversations list with full-screen scroll (SMS-style) - plass under fast banner på mobil; padding clears fixed header + safe-area; xl has in-flow header so no top padding. pb on mobile so last items clear fixed bottom nav. iOS PWA: egne verdier (mindre nav = mindre pb, pt under banner). */}
            <div
              ref={conversationsListRef}
              className={`flex-1 overflow-y-auto ios-scroll xl:pt-0 xl:pb-0 ${
                isIosPwaList
                  ? 'pt-[calc(0.5rem+env(safe-area-inset-top,0px))] pb-[calc(9.5rem+env(safe-area-inset-bottom,0px))]'
                  : 'pt-[calc(3.5rem+env(safe-area-inset-top,0px))] pb-[calc(11.5rem+env(safe-area-inset-bottom,0px))]'
              }`}
            >
              {loading ? (
                <div className="text-center py-8">
                  <Loader size="md" className="text-purple-600" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <MessageSquare className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                  <p className="text-gray-600">Ingen samtaler ennå</p>
                  <p className="text-gray-400 mt-2 text-sm">
                    {userType === 'employer' 
                      ? 'Start en samtale ved å kontakte en bruker' 
                      : 'Vent på at en arbeidsgiver kontakter deg'}
                  </p>
                </div>
              ) : (
                conversations.map((conv) => (
                  <button
                    key={conv.userId}
                    onClick={() => handleSelectConversation(conv.userId)}
                    className={`w-full p-4 flex items-center gap-3 hover:bg-purple-50 active:bg-purple-100 transition-all duration-150 border-b border-purple-50 ${
                      selectedConversation === conv.userId
                        ? 'bg-purple-50'
                        : ''
                    }`}
                  >
                    {/* Avatar */}
                    {conv.user?.profileImage ? (
                      <img
                        src={conv.user.profileImage}
                        alt={conv.user.name}
                        className="w-14 h-14 rounded-full flex-shrink-0 shadow-md object-cover"
                      />
                    ) : (
                      <div className={`w-14 h-14 bg-gradient-to-br ${
                        conv.user?.userType === 'employer' 
                          ? 'from-blue-600 to-teal-600' 
                          : 'from-purple-600 to-pink-600'
                      } rounded-full flex items-center justify-center flex-shrink-0 shadow-md`}>
                        {conv.user?.userType === 'employer' ? (
                          <Building2 className="w-7 h-7 text-white" />
                        ) : (
                          <User className="w-7 h-7 text-white" />
                        )}
                      </div>
                    )}
                    
                    {/* Message preview */}
                    <div className="flex-1 text-left min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`truncate text-base ${
                          conv.unreadCount > 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'
                        }`}>
                          {conv.user?.username || conv.user?.name}
                        </h3>
                        {conv.lastMessage && (
                          <span className={`text-xs flex-shrink-0 ml-2 ${
                            conv.unreadCount > 0 ? 'text-purple-600 font-medium' : 'text-gray-400'
                          }`}>
                            {formatTime(conv.lastMessage.createdAt)}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between gap-2">
                        {conv.lastMessage && (
                          <p className={`truncate text-sm flex items-center gap-1 ${
                            conv.unreadCount > 0 ? 'text-gray-900 font-medium' : 'text-gray-600'
                          }`}>
                            {conv.lastMessage.senderId === currentUserId && (
                              <span className="text-gray-500">Du: </span>
                            )}
                            {conv.lastMessage.content === '[Bilde]' ? (
                              <span className="inline-flex items-center gap-1">
                                <ImageIcon className="w-3.5 h-3.5 flex-shrink-0" />
                                Bilde
                              </span>
                            ) : conv.lastMessage.content}
                          </p>
                        )}
                        
                        {conv.unreadCount > 0 && (
                          <span className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs px-2 py-1 rounded-full flex-shrink-0 min-w-[24px] text-center font-medium shadow-sm">
                            {conv.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Messages view */}
          <div className={`xl:col-span-2 flex flex-col h-full w-full overflow-hidden min-h-0 ${selectedConversation ? 'flex' : 'hidden xl:flex'}`}>
            {selectedConversation && loadingMessages && !otherUser ? (
              /* Loading state when conversation is selected but data hasn't loaded yet */
              <div className="flex-1 flex items-center justify-center pt-safe xl:pt-0 w-full">
                <div className="text-center">
                  <Loader size="xl" className="text-purple-600 mb-4" />
                  <p className="text-purple-600 text-lg font-medium">Laster samtale...</p>
                </div>
              </div>
            ) : selectedConversation && otherUser ? (
              <>
                {/* Chat header - Fixed on mobile at top (no global header in fullscreen chat). z-[60] when menu open so menu appears above blocked banner (z-50). Same size and back arrow as sub-pages. */}
                <div className={`xl:pt-0 px-4 pt-4 pb-3 xl:p-6 flex items-center justify-between flex-shrink-0 xl:relative fixed top-0 left-0 right-0 bg-white xl:z-auto border-b border-purple-100 xl:border-b-0 ${showUserMenu ? 'z-[60]' : 'z-50'}`} style={{ top: 0, paddingTop: 'calc(1rem + env(safe-area-inset-top, 0px))' }}>
                  <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                    {/* Back button for mobile - same size/pil as sub-pages */}
                    <button
                      onClick={handleCloseConversation}
                      className="xl:hidden p-2 hover:bg-gray-100 rounded-lg transition-all duration-150 active:scale-95 flex-shrink-0"
                      aria-label="Tilbake"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    
                    {otherUser?.is_admin ? (
                      <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 -ml-2 sm:-ml-3 min-w-0">
                        {otherUser?.profileImage ? (
                          <img
                            src={otherUser.profileImage}
                            alt={otherUser.name}
                            className="w-9 h-9 sm:w-12 sm:h-12 rounded-full shadow-sm object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className={`w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br ${getAvatarColor(otherUser?.name || '')} rounded-full flex items-center justify-center shadow-sm flex-shrink-0`}>
                            <span className="text-white text-sm sm:text-lg">
                              {otherUser?.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="text-left min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-purple-900 text-sm sm:text-base truncate">{otherUser?.name}</h3>
                            {isBlocked && (
                              <span className="hidden xl:inline-flex items-center gap-1.5 text-red-600 text-xs font-medium flex-shrink-0">
                                <Ban className="w-3.5 h-3.5" />
                                Du har blokkert denne brukeren
                              </span>
                            )}
                          </div>
                          <p className="text-gray-500 text-xs sm:text-sm">Talentfunn</p>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          triggerAndroidHaptic('light');
                          onViewProfile?.(selectedConversation);
                        }}
                        className="flex items-center gap-2 sm:gap-3 hover:bg-purple-50 px-2 sm:px-3 py-2 rounded-xl transition-all duration-150 active:scale-[0.99] -ml-2 sm:-ml-3 min-w-0"
                      >
                        {otherUser?.profileImage ? (
                          <img
                            src={otherUser.profileImage}
                            alt={otherUser.name}
                            className="w-9 h-9 sm:w-12 sm:h-12 rounded-full shadow-sm object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className={`w-9 h-9 sm:w-12 sm:h-12 bg-gradient-to-br ${
                            otherUser?.userType === 'employer' 
                              ? 'from-blue-600 to-teal-600' 
                              : 'from-purple-600 to-pink-600'
                          } rounded-full flex items-center justify-center shadow-sm flex-shrink-0`}>
                            {otherUser?.userType === 'employer' ? (
                              <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            ) : (
                              <User className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                            )}
                          </div>
                        )}
                        <div className="text-left min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-purple-900 hover:text-purple-700 text-sm sm:text-base truncate font-medium">
                              {otherUser?.username || otherUser?.name}
                            </h3>
                            {canUploadVideo(otherUser) && (
                              <div className="flex items-center gap-1 px-1.5 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex-shrink-0">
                                <Crown className="w-2.5 h-2.5 text-white" />
                                <span className="text-white text-[10px] font-medium">PLUSS</span>
                              </div>
                            )}
                            {isBlocked && (
                              <span className="hidden xl:inline-flex items-center gap-1.5 text-red-600 text-xs font-medium flex-shrink-0">
                                <Ban className="w-3.5 h-3.5" />
                                Du har blokkert denne brukeren
                              </span>
                            )}
                          </div>
                          {/* Show city, county or fallback to location */}
                          {(otherUser?.city || otherUser?.location) && (
                            <p className="text-gray-500 text-xs sm:text-sm truncate">
                              {otherUser?.city && otherUser?.county 
                                ? `${otherUser.city}, ${otherUser.county}`
                                : otherUser?.location}
                            </p>
                          )}
                          <p className="text-sm text-purple-600 flex items-center gap-1">
                            Klikk for å se profil
                            <ChevronRight className="w-4 h-4" />
                          </p>
                        </div>
                      </button>
                    )}
                  </div>
                  
                  <div className="relative flex-shrink-0">
                    <button
                      onClick={() => {
                        triggerAndroidHaptic('light');
                        setShowUserMenu(!showUserMenu);
                      }}
                      className="p-2 hover:bg-purple-50 rounded-full transition-all duration-150 active:scale-95"
                    >
                      <MoreVertical className="w-5 h-5 text-gray-600" />
                    </button>
                    
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-purple-100 py-2 z-[60]">
                        {isBlocked ? (
                          <button
                            onClick={() => {
                              triggerAndroidHaptic('medium');
                              unblockUser();
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-purple-50 flex items-center gap-2"
                          >
                            <Ban className="w-4 h-4" />
                            Opphev blokkering
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                triggerAndroidHaptic('medium');
                                blockUser();
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-purple-50 flex items-center gap-2 text-red-600"
                            >
                              <Ban className="w-4 h-4" />
                              Blokker bruker
                            </button>
                            <button
                              onClick={() => {
                                triggerAndroidHaptic('light');
                                setShowReportModal(true);
                                setShowUserMenu(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-purple-50 flex items-center gap-2 text-orange-600"
                            >
                              <AlertTriangle className="w-4 h-4" />
                              Rapporter bruker
                            </button>
                            <button
                              onClick={() => {
                                triggerAndroidHaptic('light');
                                setShowDeleteChatModal(true);
                                setShowUserMenu(false);
                              }}
                              className="w-full px-4 py-2 text-left hover:bg-purple-50 flex items-center gap-2 text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                              Slett samtale
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {isBlocked && (
                  <div className="xl:hidden bg-red-50 border-b border-red-100 px-6 py-3 flex items-center gap-2 text-red-700 flex-shrink-0 xl:relative fixed top-0 left-0 right-0 z-50 xl:z-auto bg-white" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 4rem)' }}>
                    <Ban className="w-5 h-5" />
                    <span>Du har blokkert denne brukeren</span>
                  </div>
                )}

                {/* ✅ Gig context banner – rett under header på alle enheter */}
                {currentGig && (
                  <div className={`bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 xl:relative fixed top-0 left-0 right-0 z-40 xl:z-auto`} style={{ top: isBlocked ? 'calc(env(safe-area-inset-top, 0px) + 4.75rem + 3rem)' : 'calc(env(safe-area-inset-top, 0px) + 4.75rem)' }}>
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Briefcase className="w-5 h-5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-purple-600 font-medium">Samtale om oppdrag:</p>
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{currentGig.title}</h4>
                    </div>
                  </div>
                )}

                {/* Messages container with relative positioning for badge - wrapper ensures scroll works on desktop (flex-1 min-h-0 overflow-hidden) */}
                {/* padding-bottom = measured height of input area so chat stays above it; platform-specific extra for iOS/Android Chrome */}
                <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                {(() => {
                  const doc = typeof document !== 'undefined' ? document : null;
                  const nav = typeof navigator !== 'undefined' ? navigator : null;
                  const isIosPwa = doc?.documentElement?.classList?.contains('ios-pwa') ?? false;
                  const isIosSafari = (doc?.documentElement?.classList?.contains('ios') ?? false) && !isIosPwa;
                  const isAndroidPwa = doc?.documentElement?.classList?.contains('android-pwa') ?? false;
                  const isAndroidChrome = !!(nav && /android/i.test(navigator.userAgent) && !isAndroidPwa);

                  // Extra bottom padding so last message is visible (platform-specific)
                  let extraBottomRem = 0;
                  let extraBottomPx = 0;
                  if (isIosSafari) {
                    extraBottomRem = 1;
                    extraBottomPx = 10; // 10px lenger opp fra bunnen
                  } else if (isIosPwa) {
                    // iOS PWA: measured composer height already offsets correctly.
                    // Keep only a small breathing room over the composer.
                    extraBottomRem = 0.5;
                    extraBottomPx = 0;
                  } else if (isAndroidChrome) {
                    extraBottomRem = 2;
                    extraBottomPx = 20; // ca 10px ekstra opp fra bunnen
                  }
                  // Android PWA: ingen ekstra (fin som den er)

                  const basePaddingBottom = inputAreaHeightPx > 0
                    ? `${inputAreaHeightPx}px`
                    : 'calc(6rem + 1.5rem + env(safe-area-inset-bottom, 0px))';
                  const extraBottom = extraBottomRem > 0 || extraBottomPx > 0
                    ? `${extraBottomRem}rem + ${extraBottomPx}px`
                    : '0px';
                  const paddingBottom = (extraBottomRem > 0 || extraBottomPx > 0)
                    ? (inputAreaHeightPx > 0 ? `calc(${inputAreaHeightPx}px + ${extraBottom})` : `calc(6rem + 1.5rem + env(safe-area-inset-bottom, 0px) + ${extraBottom})`)
                    : basePaddingBottom;

                  // Top: header + optional banners. iOS PWA = litt ned så samtalen ikke klemmes under header + 10px ekstra for bobler.
                  let topBaseRem = 5;
                  let extraTopPx = 0;
                  if (isIosPwa) {
                    topBaseRem = 5.5; // keep bubbles below fixed chat header on iOS PWA
                    extraTopPx = 10;  // ~10px ned så chatten (boblene) sitter litt lavere
                  } else if (isIosSafari && !isBlocked && !currentGig) extraTopPx = 10; // 10px ned fra toppen når ikke banner
                  else if ((isAndroidPwa || isAndroidChrome) && !isBlocked && !currentGig) topBaseRem = 5.5;
                  const paddingTop = `calc(env(safe-area-inset-top, 0px) + ${topBaseRem}rem + ${extraTopPx}px${isBlocked ? ' + 3rem' : ''}${currentGig ? ' + 4rem' : ''})`;
                  
                  // Check if desktop (xl breakpoint) - on desktop, header and input are static (in-flow), no extra padding needed
                  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1280;

                  return (
                    <div className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden ios-scroll p-3 sm:p-6 space-y-4 relative`} ref={messagesContainerRef} onScroll={handleScroll} style={{ 
                      paddingTop: isDesktop ? undefined : paddingTop,
                      paddingBottom: isDesktop ? undefined : paddingBottom
                    }}>{/* Messages - scrollable area; paddingBottom = measured height of fixed input area (warning + input) so chat never goes behind it */}
                  {/* New messages badge - floating at bottom center */}
                  {showNewMessagesBadge && (
                    <button
                      onClick={handleNewMessagesBadgeClick}
                      className="fixed bottom-24 xl:bottom-24 left-1/2 transform -translate-x-1/2 z-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-150 active:scale-[0.98] flex items-center gap-2 animate-bounce"
                    >{/* Badge positioned above input field (no bottom nav in fullscreen chat) */} 
                      <MessageSquare className="w-4 h-4" />
                      <span className="text-sm font-medium">Nye meldinger ↓</span>
                    </button>
                  )}
                  
                  {loadingMessages ? (
                    <div className="text-center py-8">
                      <Loader size="md" className="text-purple-600" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="w-12 h-12 text-purple-300 mx-auto mb-3" />
                      <p className="text-gray-600">Ingen meldinger ennå</p>
                      <p className="text-gray-400 mt-2">Send den første meldingen!</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`flex ${msg.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] sm:max-w-xs xl:max-w-md rounded-2xl break-words ${
                              msg.senderId === currentUserId
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                                : 'bg-purple-50 text-gray-900'
                            }`}
                          >
                            {msg.imageUrl && (
                              <div className="px-4 pt-3">
                                <img
                                  src={msg.imageUrl}
                                  alt="Bilde i melding"
                                  className="max-w-full max-h-64 rounded-lg object-contain cursor-pointer"
                                  loading="lazy"
                                  onClick={() => msg.imageUrl && window.open(msg.imageUrl, '_blank')}
                                  onLoad={
                                    msg.id === messages[messages.length - 1]?.id
                                      ? () => scrollToBottom('smooth')
                                      : undefined
                                  }
                                />
                              </div>
                            )}
                            {msg.content && msg.content !== '[Bilde]' && (
                              <p className="px-4 py-3">{msg.content}</p>
                            )}
                            <p
                              className={`px-4 ${(msg.content && msg.content !== '[Bilde]') || msg.imageUrl ? 'pb-3' : 'py-3'} ${msg.imageUrl && (!msg.content || msg.content === '[Bilde]') ? 'pt-2' : ''} text-xs flex items-center gap-1 ${
                                msg.senderId === currentUserId ? 'text-purple-200' : 'text-gray-500'
                              }`}
                            >
                              {msg.pending ? (
                                <>
                                  <span className="inline-block h-2 w-2 rounded-full bg-current animate-pulse"></span>
                                  Sender...
                                </>
                              ) : (
                                <>
                                  {formatTime(msg.createdAt)}
                                  {/* Show read receipts only for sent messages */}
                                  {msg.senderId === currentUserId && (
                                    <span className="ml-1 flex items-center">
                                      {msg.isRead ? (
                                        /* Double checkmark - message has been read */
                                        <>
                                          <Check className="w-3 h-3" />
                                          <Check className="w-3 h-3 -ml-1.5" />
                                        </>
                                      ) : (
                                        /* Single checkmark - message delivered but not read */
                                        <Check className="w-3 h-3" />
                                      )}
                                    </span>
                                  )}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                      
                      {isTyping && (
                        <div className="flex justify-start">
                          <div className="bg-purple-50 text-gray-900 px-4 py-3 rounded-2xl">
                            <div className="flex gap-1">
                              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                              <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Scroll anchor - extra margin to ensure last message is visible above input field */}
                      <div ref={messagesEndRef} style={{ marginBottom: '1rem' }} />
                    </>
                  )}
                    </div>
                  );
                })()}
                </div>

                {/* Message input - ref used to measure height for messages container padding */}
                <div
                  ref={inputAreaContainerRef}
                  className="p-3 sm:p-4 xl:p-6 border-t border-purple-100 flex-shrink-0 bg-white fixed bottom-0 left-0 right-0 z-50 xl:static xl:z-auto"
                  style={{
                    // iOS PWA: sit closer to the safe area/home indicator.
                    paddingBottom:
                      typeof document !== 'undefined' &&
                      document.documentElement.classList.contains('ios-pwa')
                        ? 'max(calc(env(safe-area-inset-bottom, 0px) - 10px), 0px)'
                        : '20px',
                  }}
                >{/* Input area - fixed at bottom on mobile, static on desktop */}
                  {/* Professional tone warning */}
                  <div className="mb-2 sm:mb-3 flex items-start gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-blue-50 border border-blue-200 rounded-lg">
                    <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs sm:text-sm text-blue-800">
                      Hold en profesjonell tone. Trakassering fører til utestengelse.
                    </p>
                  </div>
                  
                  {imagePreview && (
                    <div className="mb-2 relative inline-block">
                      <img
                        src={imagePreview.dataUrl}
                        alt="Forhåndsvisning"
                        className="max-h-24 rounded-lg object-contain border-2 border-purple-200"
                      />
                      <button
                        type="button"
                        onClick={() => setImagePreview(null)}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        aria-label="Fjern bilde"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                  <div className="flex items-end gap-2 sm:gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={handleImageSelect}
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sending || isBlocked}
                      aria-label="Send bilde"
                      className="p-2.5 sm:p-3 rounded-xl border-2 border-purple-200 text-purple-600 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                    >
                      <ImageIcon className="w-5 h-5" />
                    </button>
                    <textarea
                      value={messageInput}
                      onChange={(e) => handleInputChange(e.target.value)}
                      onKeyDown={(e) => {
                        // Keep Enter for newline; use Ctrl/Cmd+Enter for keyboard send.
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !sending && !isBlocked) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder={isBlocked ? 'Kan ikke sende' : 'Skriv en melding...'}
                      disabled={isBlocked}
                      enterKeyHint="enter"
                      rows={1}
                      className="flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-base border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed resize-none leading-5"
                      ref={messageInputRef}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={(!messageInput.trim() && !imagePreview) || sending || isBlocked}
                      className="px-3 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span className="hidden sm:inline">Send</span>
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="hidden xl:flex flex-1 items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 text-purple-300 mx-auto mb-4" />
                  <p className="text-gray-600 text-lg">Velg en samtale for å starte</p>
                </div>
              </div>
            )}
          </div>
        </div>
          </div>
        </div>

      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-50 p-4 pb-safe">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Rapporter bruker
              </h3>
              <button
                onClick={() => {
                  triggerAndroidHaptic('light');
                  setShowReportModal(false);
                }}
                className="p-2 hover:bg-purple-50 rounded-full transition-all duration-150 active:scale-95"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 mb-2">rsak</label>
                <select
                  value={reportReason}
                  onChange={(e) => setReportReason(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Velg årsak</option>
                  <option value="spam">Spam</option>
                  <option value="harassment">Trakassering</option>
                  <option value="inappropriate">Upassende innhold</option>
                  <option value="fake">Falsk profil</option>
                  <option value="other">Annet</option>
                </select>
              </div>
              
              <div>
                <label className="block text-gray-700 mb-2">Beskrivelse (valgfritt)</label>
                <textarea
                  value={reportDescription}
                  onChange={(e) => setReportDescription(e.target.value)}
                  placeholder="Beskriv problemet..."
                  rows={4}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    triggerAndroidHaptic('light');
                    setShowReportModal(false);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-purple-200 text-purple-600 rounded-xl hover:bg-purple-50 transition-all duration-150 active:scale-[0.98]"
                >
                  Avbryt
                </button>
                <button
                  onClick={() => {
                    triggerAndroidHaptic('medium');
                    reportUser();
                  }}
                  disabled={!reportReason}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:shadow-lg transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Send rapport
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Chat Modal */}
      {showDeleteChatModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-50 p-4 pb-safe">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                Slett samtale
              </h3>
              <button
                onClick={() => {
                  triggerAndroidHaptic('light');
                  setShowDeleteChatModal(false);
                }}
                className="p-2 hover:bg-purple-50 rounded-full transition-all duration-150 active:scale-95"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                <Trash2 className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-gray-900 mb-2">
                    Er du sikker på at du vil slette hele samtalen med <strong>{otherUser?.name}</strong>?
                  </p>
                  <p className="text-gray-600 text-sm">
                    Alle meldinger vil bli permanent slettet. Denne handlingen kan ikke angres.
                  </p>
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    triggerAndroidHaptic('light');
                    setShowDeleteChatModal(false);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-purple-200 text-purple-600 rounded-xl hover:bg-purple-50 transition-all duration-150 active:scale-[0.98]"
                >
                  Avbryt
                </button>
                <button
                  onClick={() => {
                    triggerAndroidHaptic('medium');
                    deleteConversation();
                  }}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:shadow-lg transition-all duration-150 active:scale-[0.98]"
                >
                  Slett samtale
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
